import React, { FunctionComponent } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';

export const quillRichTextFormats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'list',
  'bullet',
  'link',
] as const;

type QuillDeltaAttributeValue = string | number | boolean;

type QuillDeltaOperation = {
  insert: unknown;
  attributes?: Record<string, QuillDeltaAttributeValue>;
};

type QuillDeltaLike = {
  ops?: QuillDeltaOperation[];
};

const Delta = Quill.import('delta');
const allowedFormats = new Set<string>(quillRichTextFormats);

const whitelistClipboardDelta = (_node: unknown, delta: QuillDeltaLike) => {
  const filteredOps = (delta.ops ?? [])
    .filter((op) => typeof op.insert === 'string')
    .map((op) => {
      if (!op.attributes) {
        return op;
      }

      const filteredAttributes = Object.entries(op.attributes).reduce<Record<string, QuillDeltaAttributeValue>>(
        (carry, [key, value]) => {
          if (allowedFormats.has(key)) {
            carry[key] = value;
          }

          return carry;
        },
        {},
      );

      if (Object.keys(filteredAttributes).length === 0) {
        return { insert: op.insert };
      }

      return { ...op, attributes: filteredAttributes };
    });

  return new Delta(filteredOps);
};

export const quillRichTextToolbarModules: React.ComponentProps<typeof ReactQuill>['modules'] = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
  clipboard: {
    matchVisual: false,
    matchers: [['*', whitelistClipboardDelta]],
  },
};

export type CustomQuillEditorProps = Omit<React.ComponentProps<typeof ReactQuill>, 'theme' | 'modules' | 'formats'> & {
  theme?: string;
  modules?: React.ComponentProps<typeof ReactQuill>['modules'];
  formats?: string[];
  style?: React.CSSProperties;
};

export const CustomQuillEditor: FunctionComponent<CustomQuillEditorProps> = ({
  theme = 'snow',
  modules = quillRichTextToolbarModules,
  formats = [...quillRichTextFormats],
  style,
  ...rest
}) => <ReactQuill theme={theme} modules={modules} formats={formats} {...rest} style={style} />;
